import { call, put, takeLatest, takeEvery, delay } from 'redux-saga/effects';
import i18n from '../locale';
import Api from '../utils/api';
import {
  createUserProfileApiObject,
  createChangeEmailApiObject,
  createChangePhoneApiObject,
  createVerifyEmailApiObject,
  create2FaApiObject,
  createUserPasswordApiObject,
  createChangePhoneVerifiedApiObject,
  withLoadingIndicator
} from './utils';
import * as AppActionTypes from '../redux/action/types';
import {
  SECOND_STEP,
  THIRD_STEP
} from '../components/profile/change-email-process';

/**
 * Processes update profile request
 *
 * @param {*} data
 * @author jozef.repan
 */
function* updateUserProfile(data) {
  const infoModalContent = {
    isOpen: true,
    type: 'success',
    message: i18n.t('components:modal.personal.message'),
    callback: {
      fnc: data.button.callback,
      btnText: data.button.text
    }
  };

  try {
    const response = yield call(() =>
      Api.setUserData(createUserProfileApiObject(data))
    );

    yield put({
      type: AppActionTypes.UPDATE_USER_PROFILE_SUCCESS,
      data: {
        response: response.data,
        infoModal: infoModalContent
      }
    });
  } catch (e) {
    infoModalContent.isOpen = true;
    infoModalContent.type = 'error';
    infoModalContent.message = i18n.t('components:modal.error.message');

    yield put({
      type: AppActionTypes.UPDATE_USER_PROFILE_FAILURE,
      data: {
        infoModal: infoModalContent
      }
    });
  }

  // do something after finishing request..
  if (data.callback !== undefined) {
    data.callback();
  }
}

function* updateEmailAddress(email, currentStep, callback) {
  try {
    const response = yield call(() =>
      Api.setUserData(createChangeEmailApiObject(email))
    );

    yield delay(500);

    yield put({
      type: AppActionTypes.CHANGE_EMAIL_SUCCESS,
      data: {
        response: response.data
      }
    });
    callback([], currentStep + 1);
  } catch (e) {
    yield put({
      type: AppActionTypes.CHANGE_EMAIL_FAILURE,
      data: e.data.message_code
    });

    callback([i18n.t('pages:profile.email.existingEmail')], currentStep);
  }
}

function* verifyEmailCode(email, code, currentStep, callback) {
  try {
    const response = yield call(() =>
      Api.verifyEmail(createVerifyEmailApiObject(email, code))
    );

    yield delay(500);

    yield put({
      type: AppActionTypes.VERIFY_EMAIL_CODE_SUCCESS,
      data: {
        response: response.data
      }
    });

    if (currentStep === THIRD_STEP) {
      yield put({
        type: AppActionTypes.UPDATE_EMAIL_ADDRESS,
        data: email
      });
    }
    callback([], currentStep + 1);
  } catch (e) {
    yield put({
      type: AppActionTypes.VERIFY_EMAIL_CODE_FAILURE,
      data: e.data.message_code
    });

    callback([i18n.t('pages:profile.email.wrongCode')], currentStep);
  }
}

function* updatePhoneNUmber(phoneNumber, callback) {
  try {
    const response = yield call(() =>
      Api.setUserData(createChangePhoneApiObject(phoneNumber))
    );

    yield delay(500);

    yield put({
      type: AppActionTypes.CHANGE_PHONE_NUMBER_SUCCESS,
      data: {
        response: response.data
      }
    });

    if (callback) {
      callback([], SECOND_STEP);
    }
  } catch (e) {
    yield put({
      type: AppActionTypes.CHANGE_PHONE_NUMBER_FAILURE,
      data: e.data.message_code
    });
  }
}

function* verifyPhoneCode(phone, code, currentStep, callback) {
  try {
    const response = yield call(() =>
      Api.verifyPhone(createChangePhoneVerifiedApiObject(phone, code))
    );

    yield delay(500);

    yield put({
      type: AppActionTypes.VERIFY_PHONE_NUMBER_CODE_SUCCESS,
      data: {
        response: response.data
      }
    });

    callback([], currentStep + 1);
  } catch (e) {
    yield put({
      type: AppActionTypes.VERIFY_PHONE_NUMBER_CODE_FAILURE,
      data: e.data.message_code
    });

    callback([i18n.t('pages:profile.email.wrongCode')], currentStep);
  }
}

/**
 * Processes update password request
 *
 * @param {*} data
 * @author jozef.repan
 */
function* updateUserPassword(data) {
  const infoModalContent = {
    isOpen: true,
    type: 'success',
    message: i18n.t('components:modal.password.message'),
    callback: {
      fnc: data.button.callback,
      btnText: data.button.text
    }
  };

  try {
    yield call(() => Api.setUserPassword(createUserPasswordApiObject(data)));
    yield put({
      type: AppActionTypes.UPDATE_USER_PASSWORD_SUCCESS,
      data: {
        infoModal: infoModalContent
      }
    });
  } catch (e) {
    infoModalContent.isOpen = true;
    infoModalContent.type = 'error';
    infoModalContent.message = i18n.t('components:modal.error.message');

    if (
      e.data !== undefined &&
      e.data.code === 7010 &&
      e.data.message_code === 'wrong_user_credentials'
    ) {
      infoModalContent.message = i18n.t(
        'components:modal.password.passwordMatch'
      );
    }

    yield put({
      type: AppActionTypes.UPDATE_USER_PASSWORD_FAILURE,
      data: {
        infoModal: infoModalContent
      }
    });
  }

  // do something after finishing request..
  if (data.callback !== undefined) {
    data.callback();
  }
}

function* update2FaOption({ type, value, callback, verifications }) {
  const infoModalContent = {};
  try {
    // if we are changing SMS and we are trying to enable IT
    if (type === 'sms' && value === true) {
      // check if we need to verify phone number first
      if (verifications.phone_number === true) {
        // we can disable / enable it without verification, put SUCCESS
        yield call(() => Api.set2FAOpts(create2FaApiObject(type, value)));
        yield put({
          type: AppActionTypes.UPDATE_2FA_SETTINGS_SUCCESS,
          data: {
            type,
            value
          }
        });
      } else {
        // we need to do verification first, so we send an SMS with code to user and show
        // him modal -> callback() function
        yield call(() => Api.sendSMSVerification());
        callback();
      }
    }

    // handle TOTP 2FA authentication, in this case especially Google Auth
    else if (type === 'totp' && value === true) {
      if (verifications.totp === true) {
        // we can disable / enable it without verification, put SUCCESS
        yield call(() => Api.set2FAOpts(create2FaApiObject(type, value)));
        yield put({
          type: AppActionTypes.UPDATE_2FA_SETTINGS_SUCCESS,
          data: {
            type,
            value
          }
        });
      } else {
        // we should render image and provide an input for our code
        const totpResponse = yield call(() => Api.totpRegister());
        const { image, token } = totpResponse.data;
        callback(image, token);
      }
    } else {
      // no special handing (email verification), put SUCCESS
      yield call(() => Api.set2FAOpts(create2FaApiObject(type, value)));
      yield put({
        type: AppActionTypes.UPDATE_2FA_SETTINGS_SUCCESS,
        data: {
          type,
          value
        }
      });
    }
    // delay to animation
    yield delay(300);
  } catch (e) {
    infoModalContent.isOpen = true;
    infoModalContent.type = 'error';
    infoModalContent.message = i18n.t('components:modal.error.message');

    if (
      e.data !== undefined &&
      e.data.code === 7010 &&
      e.data.message_code === 'wrong_user_credentials'
    ) {
      infoModalContent.message = i18n.t(
        'components:modal.password.passwordMatch'
      );
    }

    yield put({
      type: AppActionTypes.UPDATE_2FA_SETTINGS_FAILURE,
      data: {
        infoModal: infoModalContent
      }
    });
  }
}

function* sendSMSVerificationCode({ value: smsCode, callback }) {
  try {
    // API is weird here .. we don't need to parse anything
    yield call(() => Api.authenticateSMSVerification({ code: smsCode }));
    callback.ok();
    yield put({
      type: AppActionTypes.UPDATE_2FA_SETTINGS_SUCCESS,
      data: {
        type: 'sms',
        value: true
      }
    });
  } catch (e) {
    callback.nok(e);
  }
}

function* sendTotpVerificationCode({ value: totpCode, callback }) {
  try {
    yield call(() => Api.totpAuthenticate({ token: totpCode }));
    callback.ok();
    yield put({
      type: AppActionTypes.UPDATE_2FA_SETTINGS_SUCCESS,
      data: {
        type: 'totp',
        value: true
      }
    });
  } catch (e) {
    callback.nok(e);
  }
}

function* fetchUserData() {
  try {
    const res = yield call(() => Api.getUserData());
    yield put({
      type: AppActionTypes.SET_USER_DATA,
      data: {
        data: res.data
      }
    });
  } catch (e) {
    console.log(e);
  }
}

/**
 * Default export
 */
export default function* userProfileSaga() {
  yield takeLatest(AppActionTypes.VERIFY_EMAIL_CODE_REQUEST, ({ data }) => {
    return verifyEmailCode(
      data.email,
      data.code,
      data.currentStep,
      data.callback
    );
  });

  yield takeLatest(AppActionTypes.CHANGE_EMAIL_REQUEST, ({ data }) => {
    return updateEmailAddress(data.email, data.currentStep, data.callback);
  });

  yield takeLatest(AppActionTypes.CHANGE_PHONE_NUMBER_REQUEST, ({ data }) => {
    return updatePhoneNUmber(data.phoneNumber, data.callback);
  });

  yield takeLatest(
    AppActionTypes.VERIFY_PHONE_NUMBER_CODE_REQUEST,
    ({ data }) => {
      return verifyPhoneCode(
        data.phoneNumber,
        data.code,
        data.currentStep,
        data.callback
      );
    }
  );

  yield takeLatest(
    AppActionTypes.UPDATE_USER_PROFILE_REQUEST,
    withLoadingIndicator(({ data }) => {
      return updateUserProfile(data);
    })
  );

  yield takeLatest(
    AppActionTypes.UPDATE_USER_PASSWORD_REQUEST,
    withLoadingIndicator(({ data }) => {
      return updateUserPassword(data);
    })
  );

  yield takeEvery(
    AppActionTypes.UPDATE_2FA_SETTINGS_REQUEST,
    withLoadingIndicator(({ data }) => {
      return update2FaOption(data);
    })
  );

  yield takeLatest(
    AppActionTypes.SEND_PHONE_NUM_VERIF_CODE_REQUEST,
    ({ data }) => sendSMSVerificationCode(data)
  );

  yield takeLatest(AppActionTypes.SEND_TOTP_VERIF_CODE_REQUEST, ({ data }) =>
    sendTotpVerificationCode(data)
  );

  yield takeLatest(AppActionTypes.GET_USER_DATA, () => {
    return fetchUserData();
  });
}
